// hooks/useCache.js
import { useRef, useCallback } from 'react';

/**
 * Simple in-memory cache using useRef.
 * Data lives as long as the component/context holding this hook is mounted.
 *
 * Usage:
 *   const { get, set, invalidate, clear } = useCache();
 *   const cached = get('suppliers');
 *   if (!cached) { fetch... then set('suppliers', data); }
 */
export const useCache = () => {
  const cache = useRef({});

  const get = useCallback((key) => {
    const entry = cache.current[key];
    if (!entry) return null;

    // TTL check — if expired, remove and return null
    if (entry.expiresAt && Date.now() > entry.expiresAt) {
      delete cache.current[key];
      return null;
    }

    return entry.data;
  }, []);

  const set = useCallback((key, data, ttlMs = 5 * 60 * 1000) => {
    // ttlMs default: 5 minutes. Pass Infinity to never expire.
    cache.current[key] = {
      data,
      expiresAt: ttlMs === Infinity ? null : Date.now() + ttlMs,
    };
  }, []);

  const invalidate = useCallback((key) => {
    delete cache.current[key];
  }, []);

  const clear = useCallback(() => {
    cache.current = {};
  }, []);

  return { get, set, invalidate, clear };
};